/**
 * CR-5534 cell detail modal, extracted from ApprovalRegister.tsx by CR-7750.
 *
 * The page breached the 500-line budget once the supplier submission and
 * customer review flows landed. Behaviour is unchanged for the parts that
 * existed before; what is new is the pending block at the end, which shows
 * EITHER the customer's Accept / Reject panel or the supplier's "with your
 * customer" notice, depending on who is looking.
 *
 * The approved values stay above it. A submission never rewrites them here —
 * only the customer's accept does, server-side.
 */
import { Pencil, Trash2 } from 'lucide-react';

import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DetailCertificateActions,
  DetailRow,
  STATUS_STYLES,
} from '../ApprovalRegisterCell';
import type { ApprovalRegisterCell, ApprovalRegisterPending } from '../../lib/api';
import PendingReviewPanel from './PendingReviewPanel';
import SupplierPendingNotice from './SupplierPendingNotice';

export interface CellMeta {
  cell: ApprovalRegisterCell;
  supplierName: string;
  accreditationName: string;
}

export interface CellDetailDialogProps {
  meta: CellMeta | null;
  isCustomer: boolean;
  formatDate: (value: string) => string;
  onClose: () => void;
  onEdit: (bindingId: number) => void;
  onRequestDelete: (bindingId: number) => void;
  /** Hands back the whole submission — amend needs its id and values, not
   *  just which accreditation it was against. */
  onAmendSubmission: (pending: ApprovalRegisterPending) => void;
  onReviewed: () => void;
}

export default function CellDetailDialog({
  meta,
  isCustomer,
  formatDate,
  onClose,
  onEdit,
  onRequestDelete,
  onAmendSubmission,
  onReviewed,
}: CellDetailDialogProps) {
  const fmt = (value: string | null | undefined) => (value ? formatDate(value) : '—');

  return (
    <Dialog open={!!meta} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {meta && (
          <>
            <DialogHeader>
              <DialogTitle>{meta.accreditationName}</DialogTitle>
              <DialogDescription>{meta.supplierName}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <DetailRow label="Status" value={STATUS_STYLES[meta.cell.status].label} />
              <DetailRow label="Issued Date" value={fmt(meta.cell.issued_date)} />
              <DetailRow label="Expiry Date" value={fmt(meta.cell.expiry_date)} />
              <DetailRow
                label="Certificate Number"
                value={meta.cell.certificate_number || '—'}
              />
              {meta.cell.has_certificate ? (
                <DetailCertificateActions bindingId={meta.cell.id} />
              ) : (
                <DetailRow label="Certificate File" value="—" />
              )}
              {meta.cell.notes && (
                <div className="pt-1">
                  <div className="text-muted-foreground mb-1">Notes</div>
                  <div className="whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-3 text-foreground">
                    {meta.cell.notes}
                  </div>
                </div>
              )}
              {meta.cell.pending && (
                isCustomer ? (
                  <PendingReviewPanel
                    pending={meta.cell.pending}
                    current={meta.cell}
                    formatDate={fmt}
                    onReviewed={onReviewed}
                  />
                ) : (
                  <SupplierPendingNotice
                    pending={meta.cell.pending}
                    formatDate={fmt}
                    onAmend={onAmendSubmission}
                    onChanged={onReviewed}
                  />
                )
              )}
            </div>
            <DialogFooter className="gap-2">
              {isCustomer && (
                <>
                  <Button variant="outline" onClick={() => onRequestDelete(meta.cell.id)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                  <Button onClick={() => onEdit(meta.cell.id)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                </>
              )}
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
